import { useEffect, useState } from "react";

// Function to fetch static data from a given source
export const fetchStaticData = async (
	source: DogsTrust.StaticDataSource,
): Promise<
	| string[]
	| DogsTrust.SignPostCardProps[]
	| DogsTrust.PromotionCardToDisplayProps[]
	| DogsTrust.SponsorDogPromoCardSquareProps[]
	| DogsTrust.DogClassRegion[]
> => {
	if (process.env.GATSBY_IS_PREVIEW === "true") {
		return fetch(
			`${process.env.GATSBY_PUBLISHED_BASE_URL}/staticData/${source}.json`,
			{
				method: "GET",
				credentials: "include",
				mode: "cors",
			},
		).then((res) => res.json());
	}

	return fetch(`/staticData/${source}.json`).then((res) => res.json());
};

// Custom hook to load static data from multiple sources
export const useStaticData = (
	dataSourcesRequired: DogsTrust.StaticDataSource[],
): DogsTrust.StaticData & {
	isLoaded: boolean;
} => {
	const [data, setData] = useState<DogsTrust.StaticData>({});
	const [loadingState, setLoadingState] = useState<
		"ready" | "loading" | "loaded"
	>("ready");

	useEffect(() => {
		const loadAllData = async () => {
			if (loadingState !== "ready") return;

			// Check if static data is already available in session storage
			const sessionData: DogsTrust.StaticData = JSON.parse(
				sessionStorage.getItem("staticData") || JSON.stringify(data),
			);

			// If session data is available and all required data sources are present, use it
			if (
				dataSourcesRequired.every(
					(source) =>
						typeof sessionData[source] !== "boolean" &&
						sessionData[source]?.length,
				)
			) {
				setData({ ...sessionData });
				setLoadingState("loaded");
				return;
			}

			setLoadingState("loading");
			const staticData: DogsTrust.StaticData = { ...sessionData };

			// Fetch data from each required data source
			for (const dataSource of dataSourcesRequired) {
				const data = await fetchStaticData(dataSource);
				switch (dataSource) {
					case "dogBreedNames":
						staticData.dogBreedNames = data as string[];
						break;
					case "navPromos":
						staticData.navPromos =
							data as DogsTrust.PromotionCardToDisplayProps[];
						break;
					case "dogListingPromos":
						staticData.dogListingPromos =
							data as DogsTrust.PromotionCardToDisplayProps[];
						break;
					case "sponsorDogs":
						staticData.sponsorDogs =
							data as DogsTrust.SponsorDogPromoCardSquareProps[];
						break;
					case "dogBreeds":
						staticData.dogBreeds =
							data as unknown as Queries.BreedDataFragment[];
						break;
					case "ourStoriesProps":
						staticData.ourStoriesProps = data as DogsTrust.SignPostCardProps[];
						break;
					case "volunteerRoles":
						staticData.volunteerRoles = data as DogsTrust.SignPostCardProps[];
						break;
					case "dogSheddingNames":
						staticData.dogSheddingNames = data as string[];
						break;
					case "dogSizeNames":
						staticData.dogSizeNames = data as string[];
						break;
					case "dogActivityLevels":
						staticData.dogActivityLevels = data as string[];
						break;
					default:
						break;
				}
			}

			// Write the static data to session storage
			sessionStorage.setItem("staticData", JSON.stringify(staticData));
			setLoadingState("loaded");
			setData({ ...staticData });
		};

		loadAllData();
	}, [dataSourcesRequired, data, loadingState]);

	// Return the static data along with an "isLoaded" flag
	return {
		dogBreedNames: [],
		dogSheddingNames: [],
		dogSizeNames: [],
		dogActivityLevels: [],
		dogBreeds: [],
		ourStoriesProps: [],
		navPromos: [],
		dogListingPromos: [],
		sponsorDogs: [],
		volunteerRoles: [],
		...data,
		isLoaded: loadingState === "loaded",
	};
};
